'use client';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@ahafood/ui/components/avatar';
import { cn } from '@ahm/common-helpers';
import { motion } from 'framer-motion';
import React, { useMemo } from 'react';

import type { FoodOrderCategory } from '@/api/ahafood/get-category-fn';
import FreeModeSwiper from '@/app/[locale]/(main)/store/components/items/free-mode-swiper';
import { Link } from '@/i18n/routing';
import { trackEvent } from '@/lib/event-tracking';
import { useIsMobile } from '@/lib/is-mobile';
import { useAuthStore } from '@/store';

type StoreCategoryProps = {
  data?: FoodOrderCategory[];
  city: string;
};

export default function NewStoreCategory({ data, city }: StoreCategoryProps) {
  const isMobile = useIsMobile();
  const { user } = useAuthStore();
  const categoryItems = useMemo(() => {
    if (!data) return null;
    const allCategory = {
      id: 'all',
      slug: '',
      title_vi_vn: 'Tất cả',
      icon_img_url: '/static/images/all-category.webp',
    };
    const handleClick = (c: string) => {
      trackEvent('click_button', {
        button_name: 'category_toggle',
        selected_category: c,
        user_id: user?.cunstomerId,
      });
    };
    const categories = [allCategory, ...data];
    return categories.map((c) => (
      <FreeModeSwiper.Slide
        key={c.id}
        style={{
          width: isMobile ? 90 : 152,
          minHeight: isMobile ? 140 : 180,
          flexShrink: 0,
        }}
      >
        <Link
          onClick={() => handleClick(c.slug)}
          key={c.id}
          href={`/${city}/danh-sach-cua-hang-${c.slug}`}
          className="shrink-0 group block size-full"
        >
          {/* <div className="bg-primary-10 border border-primary-30 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 p-4 size-full flex flex-col items-center justify-center group-hover:scale-105">
            <div className="size-20 rounded-full overflow-hidden mb-2 bg-white">
              <img
                src={c.icon_img_url || '/placeholder.svg'}
                alt={c.title_vi_vn}
                width={80}
                height={80}
                className="size-full object-cover"
              />
            </div>
            <p className="text-xs text-center font-medium leading-tight text-balance">
              {c.title_vi_vn}
            </p>
          </div> */}
          <div
            className={cn(
              'relative flex size-full flex-col items-start justify-center space-y-2 p-0'
            )}
            data-category={c.slug}
          >
            <motion.div
              whileHover={{
                scale: 1.1,
                transition: {
                  type: 'spring',
                  stiffness: 300,
                  damping: 10,
                },
              }}
              className={cn(
                'group relative aspect-square w-full flex-none overflow-hidden rounded-full transition-all hover:bg-white'
              )}
            >
              <div
                className={cn(
                  'absolute z-[1] size-full rounded-full bg-transparent'
                )}
              ></div>
              <Avatar className="scale-110 overflow-hidden rounded-full size-full">
                <AvatarImage
                  src={c.icon_img_url || '/static/images/fastfood-logo.webp'}
                  alt={c.slug}
                  style={{ objectFit: 'cover' }}
                  className="size-full"
                />
                <AvatarFallback className="size-full">
                  {c.title_vi_vn ? c.title_vi_vn.charAt(0) : 'C'}
                </AvatarFallback>
              </Avatar>
            </motion.div>
            <h4
              className={cn(
                'line-clamp-2 w-full px-1 text-center text-sm font-bold text-neutral-60 group-hover:text-neutral-80 md:mx-0 md:text-lg'
              )}
            >
              {c.title_vi_vn}
            </h4>
          </div>
        </Link>
      </FreeModeSwiper.Slide>
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city, data, isMobile]);

  if (data?.length === 0 || !data) return null;

  return (
    <StoreCategoryWrapper isSearchPage={false}>
      <div className="relative z-[5]">
        <FreeModeSwiper
          navigation={false}
          className={cn('w-full !overflow-visible', isMobile ? 'h-40' : 'h-48')}
          spaceBetween={8}
          slidesPerView="auto"
          freeMode={{
            enabled: true,
            sticky: false,
          }}
        >
          {categoryItems}
        </FreeModeSwiper>
      </div>
    </StoreCategoryWrapper>
  );
}

interface StoreCategoryWrapperProps {
  children: React.ReactNode;
  isSearchPage?: boolean;
}

export function StoreCategoryWrapper({
  children,
  isSearchPage,
}: StoreCategoryWrapperProps) {
  return (
    <section className="bg-gradient-to-b from-primary-20 to-white">
      <div
        className={cn('mx-auto max-w-screen-xl px-4 pb-6 md:px-6 md:pb-12', {
          'pt-8 md:pt-20': !isSearchPage,
          'pt-4 md:pt-10': isSearchPage,
        })}
      >
        {!isSearchPage && (
          <h2 className="mb-6 space-x-2 text-2xl font-bold drop-shadow-sm md:mb-12 md:text-5xl">
            <span>🔍</span>
            <span>Hôm nay ăn gì?</span>
          </h2>
        )}
        {children}
      </div>
    </section>
  );
}
